import React, { useMemo } from "react";
import { Package } from "lucide-react";
import { DD_MM_YYYY, timeFormat } from "src/helpers";
import { Product } from "src/types";

interface CheckInProductReceiptsTableProps {
  products: Product[];
  productConfirmationMap?: Map<string, number>;
  childReceivedQuantitiesMap?: Map<string, number>;
  isChild?: boolean;
  hasChildPOs?: boolean;
  isVendorUser?: boolean;
  hasCheckIns: boolean;
}

const formatQty = (value: number | undefined) => (typeof value === "number" ? value.toLocaleString() : "—");
const getDifferenceClasses = (difference: number) => (difference === 0 ? "text-success-700" : "text-error-700");
const formatProductReceivedAt = (timestamp?: string) => {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  return `${DD_MM_YYYY.formatDate(date)} · ${timeFormat(date)}`;
};

export const CheckInProductReceiptsTable: React.FC<CheckInProductReceiptsTableProps> = ({
  products,
  productConfirmationMap,
  childReceivedQuantitiesMap,
  isChild = false,
  hasChildPOs = false,
  isVendorUser = false,
  hasCheckIns
}) => {
  const showConfirmedQty = !!productConfirmationMap;
  const showChildReceivedQty = !isChild && hasChildPOs;

  const rows = useMemo(
    () =>
      products.map((product) => {
        const confirmedQty = productConfirmationMap?.get(product.id);
        const orderQty = product.quantity ?? 0;
        const receivedQty = product.receivedQty ?? 0;
        const childReceivedQty = childReceivedQuantitiesMap?.get(product.id) ?? 0;
        const expectedQty = confirmedQty ?? orderQty;
        const totalReceivedQty = isChild ? receivedQty : receivedQty + childReceivedQty;
        const qtyDifference = totalReceivedQty - expectedQty;

        return {
          productId: product.id,
          productName: product.name || product.skuName,
          sku: product.skuName,
          barcode: product.barcode,
          orderQty,
          confirmedQty,
          receivedQty,
          childReceivedQty,
          qtyDifference,
          lastReceivedAt: product.lastReceivedAt
        };
      }),
    [products, productConfirmationMap, childReceivedQuantitiesMap, isChild]
  );

  if (!products.length) return null;

  return (
    <div className="bg-white border border-secondary-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-4 h-4 text-primary-600" />
        <h4 className="text-sm font-semibold text-secondary-800">Product Receipts</h4>
      </div>
      {!hasCheckIns ? (
        <p className="text-xs text-secondary-500 mb-3">
          No warehouse check-ins recorded yet. Received quantities will update after the first check-in.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow-sm ring-1 ring-secondary-200 rounded-lg">
              <table className="min-w-full divide-y divide-secondary-200 text-sm text-secondary-700">
                <thead className="bg-secondary-50">
                  <tr>
                    {!isVendorUser && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                        Product ID
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap min-w-[200px]">
                      Product Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                      SKU
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                      Barcode
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                      Order Qty
                    </th>
                    {showConfirmedQty && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                        Confirmed Qty
                      </th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                      Received Qty
                    </th>
                    {showChildReceivedQty && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                        Child Received Qty
                      </th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                      <span className="block">Qty Difference</span>
                      <span className="block text-[10px] font-normal text-secondary-400 mt-0.5">
                        {showChildReceivedQty ? "(Total Received - Expected)" : "(Received - Expected)"}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider whitespace-nowrap">
                      Last Received
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {rows.map(
                    ({
                      productId,
                      productName,
                      sku,
                      barcode,
                      orderQty,
                      confirmedQty,
                      receivedQty,
                      childReceivedQty,
                      qtyDifference,
                      lastReceivedAt
                    }) => (
                      <tr key={productId} className="hover:bg-secondary-50 transition-colors">
                        {!isVendorUser && (
                          <td className="px-4 py-3 font-mono text-xs text-secondary-500 whitespace-nowrap">
                            {productId}
                          </td>
                        )}
                        <td className="px-4 py-3 font-medium text-secondary-900 whitespace-nowrap">{productName}</td>
                        <td className="px-4 py-3 text-secondary-700 whitespace-nowrap">{sku || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-secondary-600 whitespace-nowrap">
                          {barcode || "—"}
                        </td>
                        <td className="px-4 py-3 text-right text-secondary-700 whitespace-nowrap">
                          {orderQty.toLocaleString()}
                        </td>
                        {showConfirmedQty && (
                          <td className="px-4 py-3 text-right text-secondary-700 whitespace-nowrap">
                            {formatQty(confirmedQty)}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right font-semibold text-secondary-900 whitespace-nowrap">
                          {receivedQty.toLocaleString()}
                        </td>
                        {showChildReceivedQty && (
                          <td className="px-4 py-3 text-right font-semibold text-secondary-900 whitespace-nowrap">
                            {childReceivedQty.toLocaleString()}
                          </td>
                        )}
                        <td
                          className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${getDifferenceClasses(qtyDifference)}`}
                        >
                          {qtyDifference === 0
                            ? "0"
                            : qtyDifference.toLocaleString(undefined, { signDisplay: "always" })}
                        </td>
                        <td className="px-4 py-3 text-secondary-600 whitespace-nowrap">
                          {formatProductReceivedAt(lastReceivedAt)}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
